import React, { lazy, Suspense, useEffect } from 'react';
import { MotionConfig } from 'framer-motion';
import './styles/global.css';
import { CartProvider } from './context/CartContext';
import TopBar from './components/TopBar/TopBar';
import Navbar from './components/Navbar/Navbar';
import Hero from './components/Hero/Hero';
import Marquee from './components/Marquee/Marquee';
import NewDrop from './components/NewDrop/NewDrop';
import BehindTheBakes from './components/BehindTheBakes/BehindTheBakes';
import BestSellers from './components/BestSellers/BestSellers';
import StatsBanner from './components/StatsBanner/StatsBanner';
import Menu from './components/Menu/Menu';
import About from './components/About/About';
import CustomOrder from './components/CustomOrder/CustomOrder';
import Gallery from './components/Gallery/Gallery';
import Testimonials from './components/Testimonials/Testimonials';
import Order from './components/Order/Order';
import FAQ from './components/FAQ/FAQ';
import InstaStrip from './components/InstaStrip/InstaStrip';
import Footer from './components/Footer/Footer';
import Cart from './components/Cart/Cart';
import BackToTop from './components/BackToTop/BackToTop';
import WhatsAppFloat from './components/WhatsAppFloat/WhatsAppFloat';
import BrandIntro from './components/BrandIntro/BrandIntro';
import Seo from './components/Seo/Seo';
import { homepageContent, siteContent } from './data/products';
import { ContentProvider, useContent } from './context/ContentContext';
const StudioApp = lazy(() => import('./admin/StudioApp'));

const themeVariables = {
  '--paper': 'paper',
  '--surface': 'surface',
  '--ink': 'ink',
  '--ink-soft': 'inkSoft',
  '--accent-ink': 'accentInk',
  '--accent-line': 'accentLine',
  '--blush-whisper': 'blushWhisper',
  '--blush-mid': 'blushMid',
  '--rose-ink': 'roseInk'
};

const ThemeVariables = () => {
  const { revision } = useContent();
  useEffect(() => {
    const root = document.documentElement;
    const previous = Object.fromEntries(
      Object.keys(themeVariables).map((variable) => [variable, root.style.getPropertyValue(variable)])
    );
    Object.entries(themeVariables).forEach(([variable, key]) => {
      root.style.setProperty(variable, siteContent.appearance[key]);
    });
    return () => Object.entries(previous).forEach(([variable, value]) => root.style.setProperty(variable, value));
  }, [revision]);
  return null;
};

function Storefront() {
  const { revision, status, isConnected } = useContent();
  const visible = homepageContent.sections;

  if (status === 'loading') return <main className="site-loading" role="status" aria-live="polite"><img src="/images/kriams/kriams-logo-redesign.svg" alt="KRIAM's" /><p>Loading today’s bakes…</p></main>;

  return (
    <MotionConfig reducedMotion="user">
      <CartProvider>
        <ThemeVariables />
        <Seo />
        {visible.brandIntro && <BrandIntro />}
        <div className="App">
          <a className="skip-link" href="#main-content">Skip to main content</a>
          <TopBar />
          {isConnected && status === 'offline' && <p className="site-connection-note" role="status">Menu updates are temporarily unavailable. Please confirm current prices and availability on WhatsApp.</p>}
          <Navbar />
          <main id="main-content" data-content-revision={revision}>
            {visible.hero && <Hero />}
            {visible.marquee && <Marquee />}
            {visible.newDrop && <NewDrop />}
            {visible.kitchenStory && <BehindTheBakes />}
            {visible.bestSellers && <BestSellers />}
            {visible.orderFacts && <StatsBanner />}
            {visible.menu && <Menu />}
            {visible.order && <Order />}
            {visible.customOrder && <CustomOrder />}
            {visible.gallery && <Gallery />}
            {visible.testimonials && <Testimonials />}
            {visible.founderStory && <About />}
            {visible.faq && <FAQ />}
            {visible.instagram && <InstaStrip />}
          </main>
          {visible.footer && <Footer />}
          <Cart />
          <BackToTop />
          {visible.whatsappFloat && <WhatsAppFloat />}
        </div>
      </CartProvider>
    </MotionConfig>
  );
}

function App() {
  const isStudio = typeof window !== 'undefined' && /^\/admin(?:\/|$)/.test(window.location.pathname);
  return <ContentProvider>{isStudio ? <Suspense fallback={<main role="status">Opening Studio…</main>}><StudioApp /></Suspense> : <Storefront />}</ContentProvider>;
}

export default App;
